import {
  getPresignedUrl,
  uploadJson,
  downloadJson,
  uploadPhoto,
  downloadPhoto,
  listRemoteObjects,
  getSyncConfig,
} from '@monorepo/shared';
import db from './db';

/**
 * App-specific sync service for nursery-irrigation-tracker.
 * All S3 keys are prefixed with 'nursery/' to avoid collisions with other apps.
 */

const KEY_PREFIX = 'nursery/';

/**
 * Push plants table to S3
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pushPlants(apiEndpoint) {
  const plants = await db.plants.toArray();
  await uploadJson(apiEndpoint, `${KEY_PREFIX}data/plants.json`, plants);
}

/**
 * Pull plants from S3 and merge locally (last-write-wins by updatedAt)
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pullPlants(apiEndpoint) {
  const remotePlants = await downloadJson(apiEndpoint, `${KEY_PREFIX}data/plants.json`);
  if (!remotePlants) return;

  for (const plant of remotePlants) {
    const local = await db.plants.get(plant.id);
    if (!local || plant.updatedAt >= local.updatedAt) {
      await db.plants.put(plant);
    }
  }
}

/**
 * Push logEntries table to S3
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pushLogEntries(apiEndpoint) {
  const logEntries = await db.logEntries.toArray();
  await uploadJson(apiEndpoint, `${KEY_PREFIX}data/logEntries.json`, logEntries);
}

/**
 * Pull logEntries from S3 and merge locally (last-write-wins by timestamp)
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pullLogEntries(apiEndpoint) {
  const remoteEntries = await downloadJson(apiEndpoint, `${KEY_PREFIX}data/logEntries.json`);
  if (!remoteEntries) return;

  for (const entry of remoteEntries) {
    const local = await db.logEntries.get(entry.id);
    if (!local || entry.timestamp >= local.timestamp) {
      await db.logEntries.put(entry);
    }
  }
}

/**
 * Push gridConfig table to S3
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pushGridConfig(apiEndpoint) {
  const gridConfig = await db.gridConfig.toArray();
  await uploadJson(apiEndpoint, `${KEY_PREFIX}data/gridConfig.json`, gridConfig);
}

/**
 * Pull gridConfig from S3 and merge locally
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function pullGridConfig(apiEndpoint) {
  const remoteConfig = await downloadJson(apiEndpoint, `${KEY_PREFIX}data/gridConfig.json`);
  if (remoteConfig && remoteConfig.length > 0) {
    await db.gridConfig.put(remoteConfig[0]);
  }
}

/**
 * Sync photos with S3 using 'nursery/photos/' prefix.
 * Pushes local photos that don't exist remotely, pulls remote photos that don't exist locally.
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function syncPhotos(apiEndpoint) {
  // Push local photos
  const photos = await db.photos.toArray();
  for (const photo of photos) {
    await uploadPhoto(
      apiEndpoint,
      `${KEY_PREFIX}photos/${photo.id}`,
      photo.blob,
      photo.mimeType || 'image/jpeg'
    );
    // Store photo metadata (without blob)
    await uploadJson(apiEndpoint, `${KEY_PREFIX}photos/${photo.id}.meta.json`, {
      id: photo.id,
      mimeType: photo.mimeType,
      createdAt: photo.createdAt,
    });
  }

  // Pull remote photos that don't exist locally
  const photoObjects = await listRemoteObjects(apiEndpoint, `${KEY_PREFIX}photos/`);
  const metaFiles = photoObjects.filter(o => o.key.endsWith('.meta.json'));
  for (const metaObj of metaFiles) {
    const meta = await downloadJson(apiEndpoint, metaObj.key);
    if (!meta) continue;
    const localPhoto = await db.photos.get(meta.id);
    if (!localPhoto) {
      const blob = await downloadPhoto(apiEndpoint, `${KEY_PREFIX}photos/${meta.id}`);
      if (blob) {
        await db.photos.put({
          id: meta.id,
          blob,
          mimeType: meta.mimeType,
          createdAt: meta.createdAt,
        });
      }
    }
  }
}

/**
 * Orchestrate full app-specific sync: pull remote first (merge), then push merged state.
 * @param {string} apiEndpoint - The API Gateway endpoint URL
 */
export async function syncAppData(apiEndpoint) {
  // Pull all app-specific data first (merges with local)
  await pullPlants(apiEndpoint);
  await pullLogEntries(apiEndpoint);
  await pullGridConfig(apiEndpoint);

  // Now push the merged local state back to S3
  await pushPlants(apiEndpoint);
  await pushLogEntries(apiEndpoint);
  await pushGridConfig(apiEndpoint);

  // Sync photos (push and pull)
  await syncPhotos(apiEndpoint);
}

/**
 * One-time recovery: pull data from the ORIGINAL unprefixed S3 keys.
 * Before the monorepo migration, data was stored at 'data/plants.json' etc.
 * (without the 'nursery/' prefix). This function pulls from those legacy keys
 * to recover data after a database reset.
 *
 * Only needs to be called once — after recovery, future syncs use the prefixed keys.
 */
export async function recoverFromLegacySync(apiEndpoint) {
  // Pull plants from legacy location
  const remotePlants = await downloadJson(apiEndpoint, 'data/plants.json');
  if (remotePlants) {
    for (const plant of remotePlants) {
      const local = await db.plants.get(plant.id);
      if (!local) {
        await db.plants.put(plant);
      }
    }
  }

  // Pull log entries from legacy location
  const remoteEntries = await downloadJson(apiEndpoint, 'data/logEntries.json');
  if (remoteEntries) {
    for (const entry of remoteEntries) {
      const local = await db.logEntries.get(entry.id);
      if (!local) {
        await db.logEntries.put(entry);
      }
    }
  }

  // Pull grid config from legacy location
  const remoteConfig = await downloadJson(apiEndpoint, 'data/gridConfig.json');
  if (remoteConfig && remoteConfig.length > 0) {
    const local = await db.gridConfig.get(remoteConfig[0].id);
    if (!local) {
      await db.gridConfig.put(remoteConfig[0]);
    }
  }

  // Pull photos from legacy location (no prefix)
  const photoObjects = await listRemoteObjects(apiEndpoint, 'photos/');
  const metaFiles = photoObjects.filter(o => o.key.endsWith('.meta.json'));
  for (const metaObj of metaFiles) {
    const meta = await downloadJson(apiEndpoint, metaObj.key);
    if (!meta) continue;
    const localPhoto = await db.photos.get(meta.id);
    if (!localPhoto) {
      const blob = await downloadPhoto(apiEndpoint, `photos/${meta.id}`);
      if (blob) {
        await db.photos.put({
          id: meta.id,
          blob,
          mimeType: meta.mimeType,
          createdAt: meta.createdAt,
        });
      }
    }
  }
}
